import type {
  ExtractionImpedimentReasonCode,
  ExtractPreviewResponse,
  SignificantHazardsImpediment,
  StandardExtractSection,
} from '@/shared/hooks/api/userStandards'

/** Extraction ran but the section is absent from the PDF (save empty JSON, not null). */
export const SECTION_NOT_FOUND_REASONS: ReadonlySet<ExtractionImpedimentReasonCode> =
  new Set([
    'scope_not_found',
    'hazards_not_found',
    'rrms_not_found',
    'ifu_not_found',
  ])

export function isSectionNotFoundReason(
  reason: string | undefined
): reason is ExtractionImpedimentReasonCode {
  if (reason == null) {
    return false
  }
  return SECTION_NOT_FOUND_REASONS.has(reason as ExtractionImpedimentReasonCode)
}

export function isSignificantHazardsSectionNotFound(
  impediment: SignificantHazardsImpediment | null | undefined
): boolean {
  if (impediment == null) {
    return false
  }
  if (impediment.noHazardsFound === true) {
    return true
  }
  return isSectionNotFoundReason(impediment.reason)
}

export function isGenericSectionNotFound(
  impediment: { reason?: string } | null | undefined
): boolean {
  return isSectionNotFoundReason(impediment?.reason)
}

/** No translatable content (section absent in PDF); skip translate steps in the extract dialog. */
export function isExtractPreviewSectionNotFound(
  section: StandardExtractSection,
  preview: ExtractPreviewResponse
): boolean {
  if (section === 'scope') {
    return isGenericSectionNotFound(preview.scopeImpediment)
  }
  if (section === 'significantHazards') {
    return isSignificantHazardsSectionNotFound(
      preview.significantHazardsImpediment
    )
  }
  if (section === 'informationForUse') {
    return isGenericSectionNotFound(preview.informationForUseImpediment)
  }
  if (section === 'riskReductionMeasures') {
    return isGenericSectionNotFound(preview.riskReductionMeasuresImpediment)
  }
  return false
}
